import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Input, InputNumber, Modal, Select, Switch } from 'antd';

import type { MetricFormat } from '../../config/metrics';
import type { PromoLibraryEditConfig, PromoLibraryFieldType } from '../../config/promoLibraryConfig';
import { formatMetricValue } from '../../utils/formatMetricValue';
import type { EditKpiTarget } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Value array shape matches the legacy convention: single-element for scalars,
 *  two-element `[min, max]` for range inputs. */
type EditValue = (number | string | boolean | null)[];

export interface EditKpiModalProps {
    open: boolean;
    target: EditKpiTarget | null;
    editConfig: PromoLibraryEditConfig | undefined;
    loading: boolean;
    onSubmit: (payload: EditKpiPayload) => void;
    onCancel: () => void;
}

export interface EditKpiPayload {
    kpiKey: string;
    rowId: string;
    value: number | string | boolean | null;
    fieldType: PromoLibraryFieldType;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface ValidationContext {
    fieldType: PromoLibraryFieldType;
    allowNegative: boolean;
    allowedDecimals: number | null;
    minValue: number | null;
    maxValue: number | null;
}

const NUMERIC_FIELD_TYPES = new Set<PromoLibraryFieldType>(['decimal', 'integer', 'range']);

const validateValues = (values: EditValue, ctx: ValidationContext): string | null => {
    if (!NUMERIC_FIELD_TYPES.has(ctx.fieldType)) return null;

    const nums = values.map(Number);

    if (ctx.fieldType === 'range' && nums.length >= 2 && nums[1] < nums[0]) {
        return 'Max value must be greater than or equal to min value';
    }
    if (!ctx.allowNegative && nums.some((n) => n < 0)) {
        return 'Value cannot be negative';
    }
    if (ctx.minValue !== null && nums.some((n) => n < ctx.minValue!)) {
        return `Value cannot be less than ${ctx.minValue}`;
    }
    if (ctx.maxValue !== null && nums.some((n) => n > ctx.maxValue!)) {
        return `Value cannot be greater than ${ctx.maxValue}`;
    }
    if (ctx.allowedDecimals !== null) {
        const exceedsDecimals = values.some((v) => {
            const decPart = String(v).split('.')[1];
            return decPart && decPart.length > ctx.allowedDecimals!;
        });
        if (exceedsDecimals) return `Value cannot have more than ${ctx.allowedDecimals} decimal places`;
    }
    return null;
};

const StyledModal = styled(Modal)`
    .ant-modal-header {
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
        padding: 16px !important;
    }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EditKpiModal: FC<EditKpiModalProps> = ({
    open,
    target,
    editConfig,
    loading,
    onSubmit,
    onCancel,
}) => {
    const [values, setValues] = useState<EditValue>([]);

    const fieldType = editConfig?.fieldType ?? 'decimal';
    const prefix = editConfig?.prefix ?? '';
    const suffix = editConfig?.suffix ?? '';

    // Seed the form whenever the modal opens for a new target.
    useEffect(() => {
        if (!open || !target) return;
        if (fieldType === 'boolean') {
            setValues(target.currentValue.length ? target.currentValue : [true]);
        } else if (fieldType === 'dropdown' && editConfig?.allowedValues?.length) {
            const current = target.currentValue[0];
            const valid = current != null && editConfig.allowedValues.includes(current as string | number);
            setValues(valid ? [current!] : [editConfig.allowedValues[0]]);
        } else {
            setValues(target.currentValue);
        }
    }, [open, target, fieldType, editConfig]);

    const dropdownOptions = useMemo(
        () => (editConfig?.allowedValues ?? []).map((v) => ({ label: String(v), value: v })),
        [editConfig?.allowedValues],
    );

    const handleChange = useCallback((value: number | string | boolean | null, index: number) => {
        setValues((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }, []);

    // Live validation — shown inline so the user sees the problem without clicking Apply.
    const validationError = useMemo(
        () =>
            editConfig
                ? validateValues(values, {
                      fieldType,
                      allowNegative: editConfig.allowNegative ?? false,
                      allowedDecimals: editConfig.allowedDecimals ?? null,
                      minValue: editConfig.minValue ?? null,
                      maxValue: editConfig.maxValue ?? null,
                  })
                : null,
        [values, editConfig, fieldType],
    );

    const isApplyDisabled =
        !values.length ||
        values.some((v) => v === null || v === undefined || v === '') ||
        !!validationError;

    const handleOk = useCallback(() => {
        if (loading || !target || !editConfig) return;

        const isNumeric = NUMERIC_FIELD_TYPES.has(fieldType);
        const rawValue = isNumeric ? Number(values[0]) : values[0];
        onSubmit({
            kpiKey: target.kpiKey,
            rowId: target.rowId,
            value: rawValue ?? null,
            fieldType,
        });
    }, [loading, target, editConfig, values, fieldType, onSubmit]);

    // Formatted the same way the grid cell is, so the dialog and the cell it was
    // opened from never disagree about the value the user is editing.
    const displayFormat = useMemo<MetricFormat>(
        () => ({
            // `null` means "unlimited decimals" for validation; for display fall back
            // to 2, the default the adapter applies to a precision-less decimal.
            allowedDecimals: editConfig?.allowedDecimals ?? 2,
            preUnit: prefix,
            postUnit: suffix,
        }),
        [editConfig?.allowedDecimals, prefix, suffix],
    );

    const formatScalar = useCallback(
        (raw: number | string | boolean | null): string => {
            if (raw === null || raw === '') return '';
            const numeric = typeof raw === 'number' ? raw : Number(raw);
            // Dropdown / text KPIs are not numbers; show them verbatim.
            if (typeof raw === 'boolean' || !Number.isFinite(numeric)) return `${prefix}${String(raw)}${suffix}`.trim();
            return formatMetricValue(numeric, displayFormat);
        },
        [displayFormat, prefix, suffix],
    );

    const currentValueDisplay = useMemo(() => {
        if (!target?.currentValue.length) return null;
        if (fieldType === 'boolean') return String(target.currentValue[0]) === 'true' ? 'True' : 'False';
        if (fieldType === 'range' && target.currentValue.length >= 2) {
            return `${formatScalar(target.currentValue[0])} – ${formatScalar(target.currentValue[1])}`;
        }
        return target.currentValue.map(formatScalar).join(' – ');
    }, [target, fieldType, formatScalar]);

    return (
        <StyledModal
            open={open}
            centered
            destroyOnClose
            width={460}
            title={
                <p className="text-base">
                    Edit Metric: {target?.kpiLabel}
                </p>
            }
            okText={loading ? 'Applying...' : 'Apply'}
            okButtonProps={{ disabled: isApplyDisabled || loading, style: { borderRadius: 6 } }}
            cancelButtonProps={{ disabled: loading, style: { borderRadius: 6 } }}
            onOk={handleOk}
            onCancel={loading ? undefined : onCancel}
            bodyStyle={{ padding: 0 }}
        >
            <div className="rounded-md border border-[#EAEAEC] px-6 py-5 mx-6 mb-3">
                {/* ---- Current Value ---- */}
                {currentValueDisplay ? (
                    <div className="mb-5 flex items-center gap-3 text-sm">
                        <span className="w-[110px] text-right shrink-0 text-[#71717B]">Current Value :</span>
                        <span className="text-[#71717B]">{currentValueDisplay}</span>
                    </div>
                ) : null}

                {/* ---- New Value ---- */}
                {fieldType === 'range' ? (
                    <RangeInput
                        values={values}
                        prefix={prefix}
                        suffix={suffix}
                        hasError={!!validationError}
                        onChange={handleChange}
                    />
                ) : fieldType === 'boolean' ? (
                    <BooleanInput value={values[0] as boolean} onChange={(v) => setValues([v])} />
                ) : fieldType === 'dropdown' ? (
                    <DropdownInput
                        value={values[0] as string | number}
                        options={dropdownOptions}
                        onChange={(v) => setValues([v])}
                    />
                ) : (
                    <ScalarInput
                        value={values[0]}
                        prefix={prefix}
                        suffix={suffix}
                        isNumeric={NUMERIC_FIELD_TYPES.has(fieldType)}
                        hasError={!!validationError}
                        onChange={(v) => handleChange(v, 0)}
                    />
                )}

                {/* ---- Inline validation error ---- */}
                {validationError ? (
                    <p className="mt-2 text-xs italic" style={{ color: '#ef4444' }}>{validationError}</p>
                ) : null}
            </div>
        </StyledModal>
    );
};

EditKpiModal.displayName = 'EditKpiModal';
export default EditKpiModal;

// ---------------------------------------------------------------------------
// Sub-components (file-private, presentational)
// ---------------------------------------------------------------------------

interface ScalarInputProps {
    value: number | string | boolean | null | undefined;
    prefix: string;
    suffix: string;
    isNumeric: boolean;
    hasError?: boolean;
    onChange: (value: string | number | null) => void;
}

const ScalarInput: FC<ScalarInputProps> = ({ value, prefix, suffix, isNumeric, hasError, onChange }) => (
    <div className="flex items-center gap-3 text-sm">
        <span className="w-[110px] text-right shrink-0 text-[#71717B]">New Value :</span>
        {isNumeric ? (
            <InputNumber
                value={value as number}
                onChange={onChange}
                className="w-[250px]"
                controls={false}
                status={hasError ? 'error' : undefined}
                addonBefore={prefix || undefined}
                addonAfter={suffix || undefined}
                placeholder="Enter value"
            />
        ) : (
            <Input
                value={value as string}
                onChange={(e) => onChange(e.target.value)}
                className="w-[250px]"
                status={hasError ? 'error' : undefined}
                addonBefore={prefix || undefined}
                addonAfter={suffix || undefined}
                placeholder="Enter value"
            />
        )}
    </div>
);

ScalarInput.displayName = 'ScalarInput';

interface RangeInputProps {
    values: EditValue;
    prefix: string;
    suffix: string;
    hasError?: boolean;
    onChange: (value: number | string | boolean | null, index: number) => void;
}

const RangeInput: FC<RangeInputProps> = ({ values, prefix, suffix, hasError, onChange }) => (
    <div className="flex items-center gap-5 text-sm">
        <div className="flex items-center gap-2">
            <span>Min :</span>
            <div className="flex items-center gap-1">
                {prefix ? <span className="text-[#71717B]">{prefix}</span> : null}
                <InputNumber
                    value={values[0] as number}
                    onChange={(v) => onChange(v, 0)}
                    className="w-[150px]"
                    controls={false}
                    status={hasError ? 'error' : undefined}
                />
                {suffix ? <span className="text-[#71717B]">{suffix}</span> : null}
            </div>
        </div>
        <div className="flex items-center gap-2">
            <span>Max :</span>
            <div className="flex items-center gap-1">
                {prefix ? <span className="text-[#71717B]">{prefix}</span> : null}
                <InputNumber
                    value={values[1] as number}
                    onChange={(v) => onChange(v, 1)}
                    className="w-[150px]"
                    controls={false}
                    status={hasError ? 'error' : undefined}
                />
                {suffix ? <span className="text-[#71717B]">{suffix}</span> : null}
            </div>
        </div>
    </div>
);

RangeInput.displayName = 'RangeInput';

interface BooleanInputProps {
    value: boolean;
    onChange: (value: boolean) => void;
}

const BooleanInput: FC<BooleanInputProps> = ({ value, onChange }) => (
    <div className="flex items-center gap-3 text-sm">
        <span className="w-[110px] text-right shrink-0 text-[#71717B]">New Value :</span>
        <div className="flex items-center gap-2">
            <span>False</span>
            <Switch checked={value} onChange={onChange} />
            <span>True</span>
        </div>
    </div>
);

BooleanInput.displayName = 'BooleanInput';

interface DropdownInputProps {
    value: string | number;
    options: { label: string; value: string | number }[];
    onChange: (value: string | number) => void;
}

const DropdownInput: FC<DropdownInputProps> = ({ value, options, onChange }) => (
    <div className="flex items-center gap-3 text-sm">
        <span className="w-[110px] text-right shrink-0 text-[#71717B]">New Value :</span>
        <Select
            value={value}
            options={options}
            onChange={onChange}
            className="w-[200px]"
        />
    </div>
);

DropdownInput.displayName = 'DropdownInput';
